import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import LogoutIcon from '@mui/icons-material/Logout';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import {
	Avatar,
	Box,
	Divider,
	ListItemIcon,
	Menu,
	MenuItem,
	Typography,
	CircularProgress,
} from '@mui/material';

import { ILoadingState } from 'src/components/common/types/loadingTypes';
import { logout } from 'src/features/auth/store/authSlice';
import { IProfileImageProps } from 'src/features/profile/domain/types';
import { IUserProfileState } from 'src/features/profile/domain/types';
import { getUserProfile } from 'src/features/profile/store/userProfileThunks';
import { USER_PROFILE_LOADING_KEY } from 'src/features/profile/store/userProfileThunks';
import { AppDispatch, RootState } from 'src/store';

const getInitials = (fname: string, lname: string) =>
	`${fname.charAt(0)}${lname.charAt(0)}`.toUpperCase();

const ProfileImage: React.FC<IProfileImageProps> = ({
	imageSrc,
}: IProfileImageProps): React.JSX.Element => {
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const [anchorElement, setAnchorElement] = useState<HTMLElement | null>(
		null,
	);
	const isLoading = useSelector(
		(state: { loading: ILoadingState }) =>
			state.loading.loadingStates[USER_PROFILE_LOADING_KEY],
	);
	const { profile } = useSelector(
		(state: RootState) => state.user_profile as IUserProfileState,
	);

	const menuVisible = Boolean(anchorElement);

	const handleClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorElement(event.currentTarget);
	};

	const handleClose = () => setAnchorElement(null);

	const handleSignOut = () => {
		handleClose();
		dispatch(logout());
	};

	useEffect(() => {
		if (!profile) {
			dispatch(getUserProfile({}));
		}
	}, [dispatch, profile]);

	// If profile is loading, show a loading indicator
	if (isLoading) {
		return (
			<Avatar
				sx={{
					width: 40,
					height: 40,
					marginRight: '24px',
					backgroundColor: '#BE3327',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
				data-testid='ProfileImage'
			>
				<CircularProgress size={20} />
			</Avatar>
		);
	}

	// If profile is not loaded yet, show a simple avatar
	if (!profile) {
		return (
			<Avatar
				sx={{
					width: 40,
					height: 40,
					marginRight: '24px',
					cursor: 'pointer',
					backgroundColor: '#BE3327',
				}}
				data-testid='ProfileImage'
			/>
		);
	}

	return (
		<>
			<Avatar
				sx={{
					width: 40,
					height: 40,
					marginRight: '24px',
					cursor: 'pointer',
					backgroundColor: '#BE3327',
				}}
				onClick={handleClick}
				data-testid='ProfileImage'
				aria-controls={menuVisible ? 'user-profile-menu' : undefined}
				aria-haspopup='true'
				aria-expanded={menuVisible ? 'true' : undefined}
			>
				{imageSrc ? (
					<img
						src={imageSrc}
						alt='Profile'
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'cover',
						}}
					/>
				) : (
					<Typography
						sx={{
							color: 'white',
							fontSize: '1.125rem',
							display: 'flex',
							justifyContent: 'center',
							alignItems: 'center',
							width: '100%',
							height: '100%',
						}}
					>
						{getInitials(profile.first_name, profile.last_name)}
					</Typography>
				)}
			</Avatar>
			<Menu
				id='user-profile-menu'
				anchorEl={anchorElement}
				transformOrigin={{
					vertical: 'top',
					horizontal: 'right',
				}}
				open={menuVisible}
				onClose={handleClose}
				MenuListProps={{ 'aria-labelledby': 'user-profile-button' }}
				sx={{ my: 1 }}
				disableScrollLock={true}
				data-testid='UserProfileMenu'
				anchorOrigin={{
					vertical: 'bottom',
					horizontal: 'left',
				}}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						paddingX: 2,
						paddingTop: 1,
						paddingBottom: 1.5,
					}}
				>
					<Typography color='text.primary'>
						{profile.first_name} {profile.last_name}
					</Typography>
					<Typography color='text.secondary'>
						{profile.email}
					</Typography>
				</Box>
				<Divider />
				<MenuItem
					onClick={() => {
						navigate('/user-profile');
						handleClose();
					}}
					sx={{ marginTop: 1, paddingRight: '3rem' }}
					data-testid='ProfileSettingsMenuItem'
				>
					<ListItemIcon>
						<ManageAccountsIcon />
					</ListItemIcon>
					Profile Settings
				</MenuItem>
				<Divider />
				<MenuItem
					onClick={handleSignOut}
					sx={{ marginTop: 1, paddingRight: '3rem' }}
					data-testid='ProfileSettingsSignOutItem'
				>
					<ListItemIcon>
						<LogoutIcon />
					</ListItemIcon>
					Sign out
				</MenuItem>
			</Menu>
		</>
	);
};

export default ProfileImage;
